import { useCallback, useState } from 'react';

export type ConverterError = string | ((error: unknown) => string);

export interface TextConverterOptions<TMode extends string> {
  /** Exactly two modes; the first one is the initial mode. `switchMode` swaps them. */
  modes: readonly [TMode, TMode];
  transforms: Record<TMode, (input: string) => string>;
  /** A fixed message, or a function that builds one from the thrown error. */
  errors: Record<TMode, ConverterError>;
}

export interface TextConverterState<TMode extends string> {
  input: string;
  output: string;
  mode: TMode;
  error: string | null;
  copied: boolean;
  setInput: (value: string) => void;
  setMode: (mode: TMode) => void;
  clearError: () => void;
  /** Set the input, clear the output and the error in one step. */
  reset: (value?: string) => void;
  convert: () => void;
  switchMode: () => void;
  copy: () => Promise<void>;
}

/**
 * Shared state machine for the two-way text converters (Base64, URL, XML,
 * JSON/YAML).
 *
 * Each page supplies its own transforms, error copy and mode names; the
 * input/output/mode/error/copied plumbing lives here once.
 */
export function useTextConverter<TMode extends string>({
  modes,
  transforms,
  errors,
}: TextConverterOptions<TMode>): TextConverterState<TMode> {
  const [primary, secondary] = modes;
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [mode, setMode] = useState<TMode>(primary);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const run = useCallback(
    (transform: (value: string) => string, message: ConverterError) => {
      try {
        setError(null);
        setOutput(transform(input));
      } catch (err) {
        setError(typeof message === 'function' ? message(err) : message);
        setOutput('');
      }
    },
    [input],
  );

  const convert = useCallback(() => {
    run(transforms[mode], errors[mode]);
  }, [mode, run, transforms, errors]);

  const switchMode = useCallback(() => {
    setMode(current => (current === primary ? secondary : primary));
    setInput(output);
    setOutput('');
    setError(null);
  }, [primary, secondary, output]);

  const clearError = useCallback(() => setError(null), []);

  const reset = useCallback((value = '') => {
    setInput(value);
    setOutput('');
    setError(null);
  }, []);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [output]);

  return {
    input,
    output,
    mode,
    error,
    copied,
    setInput,
    setMode,
    clearError,
    reset,
    convert,
    switchMode,
    copy,
  };
}
